import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { PatientHeader } from "../components/PatientHeader";
import { FieldGroup } from "../components/FieldGroup";
import { LenderTabs } from "../components/LenderTabs";
import { LenderPanel } from "../components/LenderPanel";
import { buildFieldDefs } from "../lib/fieldDefs";
import { LENDERS, DEMO_PATIENT, DEMO_SESSION } from "../lib/demoData";
import { useClipboard } from "../hooks/useClipboard";
import { useAuditLog } from "../hooks/useAuditLog";
import { useToastStore } from "../store/toast";
import { DEMO_MODE } from "../lib/config";
import { api } from "../lib/api";
import type { LenderId, Outcome, Patient, Session } from "../types";

export function SessionPage() {
  const { id } = useParams();
  const [activeLender, setActiveLender] = useState<LenderId>("proceed");
  const [ssnRevealed, setSsnRevealed] = useState(false);
  const [revealedSsn, setRevealedSsn] = useState<string | null>(null);
  const [localOutcomes, setLocalOutcomes] = useState<Outcome[]>([]);
  const { copiedKey, copy } = useClipboard();
  const audit = useAuditLog();
  const showToast = useToastStore((s) => s.show);

  // Load session + patient (or demo data when API unreachable)
  const { data, isLoading } = useQuery({
    queryKey: ["session", id],
    queryFn: async (): Promise<{ session: Session; patient: Patient; outcomes: Outcome[] }> => {
      if (DEMO_MODE) {
        return { session: DEMO_SESSION, patient: DEMO_PATIENT, outcomes: [] };
      }
      const session = await api.get<Session>(`/sessions/${id}`);
      const patient = await api.get<Patient>(`/patients/${session.patientId}`);
      const outcomes = await api.get<Outcome[]>(`/sessions/${id}/outcomes`);
      return { session, patient, outcomes };
    },
    enabled: Boolean(id),
  });

  const session = data?.session ?? DEMO_SESSION;
  const patient = data?.patient ?? DEMO_PATIENT;
  const serverOutcomes = data?.outcomes ?? [];
  const outcomes = useMemo(
    () => [...serverOutcomes, ...localOutcomes].sort((a, b) => a.at.localeCompare(b.at)),
    [serverOutcomes, localOutcomes],
  );

  const defs = useMemo(
    () => buildFieldDefs({ patient, session, ssn: ssnRevealed ? revealedSsn : null }),
    [patient, session, ssnRevealed, revealedSsn],
  );

  const handleCopy = async (key: string, value: string, label: string) => {
    if (key.endsWith(":ssn") || key === "ssn") {
      if (!ssnRevealed || !revealedSsn) {
        showToast("Reveal SSN before copying", "warn");
        return;
      }
    }
    const ok = await copy(key, value);
    if (ok) {
      showToast(`Copied ${label.toLowerCase()}`);
      const fieldName = key.split(":").pop() ?? key;
      audit({
        action: "copy",
        patientId: patient.id,
        sessionId: session.id,
        field: fieldName,
      });
    } else {
      showToast("Couldn't copy — check browser permissions", "warn");
    }
  };

  const handleToggleSsn = async () => {
    if (ssnRevealed) {
      setSsnRevealed(false);
      setRevealedSsn(null);
      showToast("SSN hidden");
      return;
    }
    if (!DEMO_MODE) {
      try {
        // The audit log MUST land before we reveal. The API also re-checks.
        await audit({ action: "reveal", patientId: patient.id, sessionId: session.id, field: "ssn" });
        const { ssn } = await api.post<{ ssn: string }>(
          `/patients/${patient.id}/ssn/reveal`,
          { sessionId: session.id },
        );
        setRevealedSsn(ssn);
        setSsnRevealed(true);
        showToast("SSN revealed — logged to audit trail", "warn");
        // Auto re-mask after 30s
        setTimeout(() => {
          setSsnRevealed(false);
          setRevealedSsn(null);
        }, 30_000);
      } catch (e) {
        showToast(e instanceof Error ? e.message : "Reveal failed", "warn");
      }
    } else {
      // Demo mode: a fake placeholder so the UI is exercisable
      audit({ action: "reveal", patientId: patient.id, sessionId: session.id, field: "ssn" });
      setRevealedSsn(`•••-••-${patient.ssnLast4}`.replace(/•/g, "0"));
      setSsnRevealed(true);
      showToast("SSN revealed — logged to audit trail (demo)", "warn");
      setTimeout(() => {
        setSsnRevealed(false);
        setRevealedSsn(null);
      }, 30_000);
    }
  };

  const logOutcome = useMutation({
    mutationFn: async (o: Omit<Outcome, "at">): Promise<Outcome> => {
      const payload = { ...o, at: new Date().toISOString() };
      if (DEMO_MODE) {
        return payload;
      }
      return await api.post<Outcome>(`/sessions/${session.id}/outcomes`, payload);
    },
    onSuccess: (saved) => {
      setLocalOutcomes((prev) => [...prev, saved]);
      audit({
        action: "submit_outcome",
        patientId: patient.id,
        sessionId: session.id,
        field: saved.lender,
        meta: { status: saved.status, amount: saved.amount },
      });
      showToast(
        `${saved.status.charAt(0).toUpperCase() + saved.status.slice(1)} logged for ${
          LENDERS.find((l) => l.id === saved.lender)?.name ?? saved.lender
        }`,
        saved.status === "approved" ? "ok" : "default",
      );
    },
  });

  if (isLoading) {
    return <div className="empty">Loading session…</div>;
  }

  return (
    <>
      <PatientHeader
        patient={patient}
        session={session}
        outcomes={outcomes}
        copiedKey={copiedKey}
        onCopy={handleCopy}
      />

      <main className="main">
        <section className="left">
          <div className="left__top">
            <h2 className="left__title">Patient record</h2>
            <p className="left__sub">
              Click any field to copy. Verify against the lender's portal before submitting.
            </p>
          </div>

          <FieldGroup
            title="Identity"
            keys={["fullName", "dob", "ssn", "ssnLast4"]}
            defs={defs}
            copiedKey={copiedKey}
            onCopy={handleCopy}
            ssnRevealed={ssnRevealed}
            onToggleSsn={handleToggleSsn}
          />
          <FieldGroup
            title="Contact"
            keys={["phone", "email"]}
            defs={defs}
            copiedKey={copiedKey}
            onCopy={handleCopy}
            ssnRevealed={ssnRevealed}
            onToggleSsn={handleToggleSsn}
          />
          <FieldGroup
            title="Address"
            keys={["addressLine", "addressStreet", "addressCity", "addressState", "addressZip"]}
            defs={defs}
            copiedKey={copiedKey}
            onCopy={handleCopy}
            ssnRevealed={ssnRevealed}
            onToggleSsn={handleToggleSsn}
          />
          <FieldGroup
            title="Employment & income"
            keys={["employer", "employmentLength", "annualIncome", "monthlyHousing"]}
            defs={defs}
            copiedKey={copiedKey}
            onCopy={handleCopy}
            ssnRevealed={ssnRevealed}
            onToggleSsn={handleToggleSsn}
          />
          <FieldGroup
            title="Treatment"
            keys={["requestedAmount", "treatmentNotes"]}
            defs={defs}
            copiedKey={copiedKey}
            onCopy={handleCopy}
            ssnRevealed={ssnRevealed}
            onToggleSsn={handleToggleSsn}
          />
        </section>

        <aside className="right">
          <LenderTabs
            lenders={LENDERS}
            active={activeLender}
            onChange={setActiveLender}
            outcomes={outcomes}
          />
          <LenderPanel
            lender={LENDERS.find((l) => l.id === activeLender)!}
            defs={defs}
            copiedKey={copiedKey}
            onCopy={handleCopy}
            ssnRevealed={ssnRevealed}
            onToggleSsn={handleToggleSsn}
            outcomes={outcomes}
            onLogOutcome={(o) => logOutcome.mutateAsync(o)}
          />
        </aside>
      </main>
    </>
  );
}
